import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { LegalLayoutComponent } from '../rechtliches/legal-layout.component';

interface ContentReportAck {
  reportId: string;
  createdAt: string;
}

/**
 * Notice & Action — Meldemechanismus nach Art. 16 DSA. Auch für nicht
 * eingeloggte Nutzer zugänglich. Backend filtert ContentType + ViolationType
 * gegen Whitelist und rate-limited Spam-Floods.
 */
@Component({
  selector: 'app-melden',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, LegalLayoutComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './melden.component.html',
  styleUrl: './melden.component.scss',
})
export class MeldenComponent {
  private readonly fb = inject(FormBuilder);
  private readonly http = inject(HttpClient);
  private readonly title = inject(Title);

  readonly form = this.fb.nonNullable.group({
    contentType: ['profile', [Validators.required]],
    contentId: ['', [Validators.required, Validators.pattern(uuidRegex)]],
    violationType: ['beleidigung', [Validators.required]],
    description: ['', [Validators.required, Validators.minLength(20), Validators.maxLength(4000)]],
    reporterEmail: ['', [Validators.email, Validators.maxLength(256)]],
  });

  readonly submitting = signal(false);
  readonly submitted = signal<ContentReportAck | null>(null);
  readonly errorMessage = signal<string | null>(null);

  constructor() {
    this.title.setTitle('Inhalt melden · Craftly');
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting.set(true);
    this.errorMessage.set(null);
    try {
      const payload = {
        contentType: this.form.controls.contentType.value,
        contentId: this.form.controls.contentId.value.trim(),
        violationType: this.form.controls.violationType.value,
        description: this.form.controls.description.value.trim(),
        reporterEmail: this.form.controls.reporterEmail.value.trim() || null,
      };
      const ack = await firstValueFrom(
        this.http.post<ContentReportAck>('/api/melden', payload),
      );
      this.submitted.set(ack);
    } catch (err: any) {
      const code = err?.status as number | undefined;
      if (code === 409) {
        this.errorMessage.set(
          'Du hast in der letzten Stunde sehr viele Meldungen abgegeben. Bitte später erneut versuchen.',
        );
      } else if (code === 400 && err?.error?.error) {
        this.errorMessage.set(err.error.error);
      } else {
        this.errorMessage.set('Senden fehlgeschlagen — bitte versuche es später erneut.');
      }
    } finally {
      this.submitting.set(false);
    }
  }

  reset(): void {
    this.submitted.set(null);
    this.errorMessage.set(null);
    this.form.reset({
      contentType: 'profile',
      contentId: '',
      violationType: 'beleidigung',
      description: '',
      reporterEmail: '',
    });
  }
}

const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
